import styled, { keyframes, css } from "styled-components";
import logoLapieza from "../../assets/logo-lapieza.png";
import heroAts from "../../assets/hero/ats.png";
import { palette, fonts, radii } from "../../styles/palette";

export { css };

/** Fondo degradado de marca (mismo asset que el hero de lapieza.io). */
const brandBackdrop = css`
  background-color: ${palette.white};
  background-image: url("/images/Degradado_Fondo.jpg");
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center bottom;
`;

/** Logo LaPieza fijo en la esquina superior izquierda. */
const brandLogo = css`
  &::before {
    content: "";
    position: absolute;
    top: 24px;
    left: 24px;
    width: 150px;
    height: 34px;
    background: url(${logoLapieza}) no-repeat left center / contain;
    pointer-events: none;
    z-index: 10;

    @media (max-width: 480px) {
      top: 16px;
      left: 16px;
      width: 120px;
      height: 28px;
    }
  }
`;

export const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 ${palette.redGlow}; }
  70% { box-shadow: 0 0 0 16px ${palette.redGlowNone}; }
  100% { box-shadow: 0 0 0 0 ${palette.redGlow}; }
`;

export const blink = keyframes`
  0% { opacity: 1; }
  12.5% { opacity: 0.3; }
  25% { opacity: 1; }
  37.5% { opacity: 0.3; }
  50% { opacity: 1; }
  100% { opacity: 1; }
`;

export const RefreshButton = styled.button<{
  $isBlinking: boolean;
  $isPlaying: boolean;
}>`
  align-self: center;
  background: transparent;
  border: none;
  color: ${palette.navy};
  font-size: 16px;
  cursor: ${({ $isPlaying }) => ($isPlaying ? "not-allowed" : "pointer")};
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: ${radii.pill};
  transition: all 0.2s ease;
  opacity: ${({ $isPlaying }) => ($isPlaying ? 0.5 : 1)};
  text-decoration: underline;
  text-decoration-color: ${palette.red};
  text-underline-offset: 3px;
  font-weight: 600;
  animation: ${({ $isBlinking }) =>
    $isBlinking
      ? css`
          ${blink} 2s ease-in-out
        `
      : "none"};

  &:hover:not(:disabled) {
    background: ${palette.surfaceMuted};
    color: ${palette.red};
    text-decoration: none;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

export const PreparationScreenContainer = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2000;
  ${brandBackdrop};
  display: flex;
  min-height: 100vh;
  overflow-y: auto;
  ${brandLogo};
`;

export const PreparationContent = styled.div`
  display: flex;
  flex: 1;
  max-width: 1200px;
  margin: 0 auto;
  padding: 96px 20px 40px;
  gap: 64px;
  align-items: center;
  justify-content: center;
  min-height: 100vh;

  @media (max-width: 768px) {
    flex-direction: column;
    /* Sin centrado vertical: con contenido más alto que el viewport el
       centrado desborda por arriba y tapa el logo. */
    justify-content: flex-start;
    gap: 40px;
    padding: 88px 16px 32px;
    min-height: auto;
  }

  @media (max-width: 480px) {
    gap: 30px;
    padding: 72px 12px 24px;
  }
`;

export const AudioSection = styled.div`
  position: relative;
  flex: 1 1 auto;
  flex-shrink: 0;
  max-width: 520px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 40px 30px;
  border-radius: ${radii.md};
  background: ${palette.navy};
  box-shadow: 0 18px 48px ${palette.shadowDark};
  overflow: hidden;

  /* Marca de agua de marca (hero LaPieza). */
  &::after {
    content: "";
    position: absolute;
    right: -60px;
    bottom: -60px;
    width: 220px;
    height: 220px;
    background: url(${heroAts}) no-repeat center / contain;
    opacity: 0.22;
    pointer-events: none;

    @media (max-width: 768px) {
      width: 160px;
      height: 160px;
      right: -50px;
      bottom: -50px;
    }
  }

  > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 768px) {
    padding: 28px 20px;
  }
`;

export const AudioCheckTitle = styled.h2`
  font-family: ${fonts.heading};
  font-size: 24px;
  font-weight: 600;
  color: ${palette.white};
  margin: 0 0 16px 0;
  text-align: center;
  flex-shrink: 0;

  @media (max-width: 480px) {
    font-size: 18px;
    margin: 0 0 8px 0;
  }
`;

export const AudioCheckSubtitle = styled.p`
  font-size: 16px;
  color: ${palette.gray};
  margin: 0 0 24px 0;
  text-align: center;
  line-height: 1.5;
  flex-shrink: 0;

  @media (max-width: 480px) {
    font-size: 13px;
    margin: 0 0 16px 0;
  }
`;

export const AudioVisualizer = styled.div`
  width: 240px;
  height: 240px;
  border-radius: 50%;
  background: ${palette.navyDark};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 20px 0 16px 0;
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
  border: 4px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 10px 32px rgba(35, 35, 35, 0.35);

  @media (max-width: 480px) {
    width: 180px;
    height: 180px;
    margin: 16px 0 24px 0;
  }
`;

export const AudioLevelIndicator = styled.div<{ level: number }>`
  width: ${({ level }) => Math.max(20, level * 0.8)}%;
  height: ${({ level }) => Math.max(20, level * 0.8)}%;
  border-radius: 50%;
  background: ${({ level }) =>
    level > 50
      ? palette.red
      : level > 20
      ? palette.redOnDark
      : "rgba(255, 255, 255, 0.18)"};
  transition: all 0.1s ease;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

export const AudioStatus = styled.div<{ hasPermission: boolean }>`
  font-size: 18px;
  font-weight: 600;
  color: ${({ hasPermission }) =>
    hasPermission ? palette.white : palette.redOnDark};
  margin-bottom: 24px;
  text-align: center;
  min-height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const AudioCheckButtons = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  max-width: 400px;
  flex-wrap: wrap;
  justify-content: center;
`;

export const AudioCheckButton = styled.button<{
  variant?: "primary" | "secondary";
}>`
  background: ${({ variant }) =>
    variant === "primary" ? palette.red : palette.white};
  color: ${({ variant }) =>
    variant === "primary" ? palette.white : palette.navy};
  border: ${({ variant }) =>
    variant === "primary" ? "none" : `1.5px solid ${palette.border}`};
  border-radius: ${radii.pill};
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  flex: 1;
  min-width: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  white-space: nowrap;
  height: fit-content;
  box-shadow: ${({ variant }) =>
    variant === "primary" ? `0 4px 14px ${palette.redGlow}` : "none"};

  &:hover:not(:disabled) {
    background: ${({ variant }) =>
      variant === "primary" ? palette.redHover : palette.surfaceMuted};
    color: ${({ variant }) =>
      variant === "primary" ? palette.white : palette.red};
    border-color: ${({ variant }) =>
      variant === "primary" ? "transparent" : palette.red};
    transform: translateY(-2px);
    box-shadow: ${({ variant }) =>
      variant === "primary"
        ? `0 6px 18px ${palette.redGlow}`
        : `0 6px 18px ${palette.shadowMedium}`};
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  @media (max-width: 480px) {
    padding: 10px 16px;
    font-size: 14px;
    min-width: 100px;
  }
`;

export const RequirementsSection = styled.div`
  flex: 1 1 auto;
  flex-shrink: 0;
  max-width: 520px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  background: ${palette.white};
  border: 1px solid ${palette.border};
  border-radius: ${radii.md};
  box-shadow: ${palette.boxShadowCard};
  padding: 40px 36px;

  @media (max-width: 768px) {
    padding: 28px 20px;
    margin-bottom: 24px;
  }
`;

export const PreparationTitle = styled.h1`
  font-family: ${fonts.heading};
  font-size: 28px;
  font-weight: 700;
  color: ${palette.navy};
  margin: 0 0 32px 0;
  text-align: center;
  flex-shrink: 0;

  @media (max-width: 768px) {
    font-size: 26px;
    margin: 0 0 20px 0;
  }

  @media (max-width: 480px) {
    font-size: 22px;
    margin: 0 0 16px 0;
  }
`;

export const StartButton = styled.button`
  background: ${palette.red};
  color: ${palette.white};
  border: none;
  border-radius: ${radii.pill};
  padding: 16px 40px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  margin: 40px auto 0 auto;
  transition: all 0.3s;
  box-shadow: 0 4px 14px ${palette.redGlow};
  display: block;
  width: fit-content;

  &:hover:not(:disabled) {
    background: ${palette.redHover};
    transform: translateY(-2px);
    box-shadow: 0 6px 18px ${palette.redGlow};
  }

  &:disabled {
    background: ${palette.gray};
    color: ${palette.textSecondary};
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }

  @media (max-width: 768px) {
    width: 100%;
    margin-top: 32px;
    font-size: 15px;
    padding: 14px 24px;
  }

  @media (max-width: 480px) {
    margin-top: 28px;
    font-size: 14px;
    padding: 12px 20px;
  }
`;

// Pantalla de prueba de audio
export const AudioTestScreen = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2000;
  ${brandBackdrop};
  display: flex;
  min-height: 100vh;
  overflow-y: auto;
  ${brandLogo};
`;

export const AudioTestContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 96px 20px 40px;
  max-width: 760px;
  margin: 0 auto;
`;

export const AudioTestTitle = styled.h1`
  font-family: ${fonts.heading};
  font-size: 28px;
  font-weight: 700;
  color: ${palette.navy};
  margin: 0 0 24px 0;
  text-align: center;
`;

export const AudioTestSubtitle = styled.p`
  font-size: 18px;
  color: ${palette.textSecondary};
  margin: 0 0 40px 0;
  text-align: center;
  line-height: 1.5;
`;

export const AudioTestCard = styled.div`
  background: ${palette.white};
  border: 1px solid ${palette.border};
  border-radius: ${radii.md};
  box-shadow: ${palette.boxShadowCard};
  padding: 50px 40px;
  width: 100%;
  max-width: 700px;
  text-align: center;

  @media (max-width: 480px) {
    padding: 32px 20px;
  }
`;

export const AudioTestIcon = styled.div`
  margin: 0 auto 24px;
  width: 112px;
  height: 112px;
  border-radius: 50%;
  background: ${palette.navy};
  color: ${palette.white};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 10px 28px ${palette.shadowMedium};
`;

export const AudioTestMessage = styled.div`
  font-family: ${fonts.heading};
  font-size: 20px;
  font-weight: 600;
  color: ${palette.navy};
  margin-bottom: 16px;
  line-height: 1.4;
  padding: 0 8px;
  max-width: 100%;
`;

export const AudioTestQuestion = styled.div`
  font-size: 16px;
  color: ${palette.textSecondary};
  margin-bottom: 32px;
  line-height: 1.5;
`;

export const AudioTestButtons = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
`;

export const AudioTestButton = styled.button<{
  variant: "success" | "danger" | "primary" | "secondary";
}>`
  background: ${({ variant }) =>
    variant === "danger" || variant === "secondary"
      ? palette.white
      : palette.red};
  color: ${({ variant }) =>
    variant === "danger" || variant === "secondary"
      ? palette.navy
      : palette.white};
  border: ${({ variant }) =>
    variant === "danger" || variant === "secondary"
      ? `1.5px solid ${palette.navy}`
      : "none"};
  border-radius: ${radii.pill};
  padding: 14px 24px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  flex: 1;
  transition: all 0.3s;
  box-shadow: ${({ variant }) =>
    variant === "danger" || variant === "secondary"
      ? "none"
      : `0 4px 14px ${palette.redGlow}`};

  &:hover:not(:disabled) {
    background: ${({ variant }) =>
      variant === "danger" || variant === "secondary"
        ? palette.navy
        : palette.redHover};
    color: ${palette.white};
    transform: translateY(-1px);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
    transform: none;
  }

  @media (max-width: 480px) {
    padding: 10px 16px;
    font-size: 14px;
  }
`;

export const RequirementIcon = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
  margin-top: 2px;
  color: ${palette.red};
`;

export const RequirementText = styled.p`
  font-size: 16px;
  color: ${palette.textSecondary};
  margin: 0;
  line-height: 1.5;

  strong {
    color: ${palette.navy};
    font-weight: 600;
  }

  @media (max-width: 480px) {
    font-size: 14px;
    line-height: 1.4;
  }
`;

export const RequirementItem = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 18px;
  gap: 14px;
  padding: 0 8px;

  &:last-of-type {
    margin-bottom: 0;
  }
`;

export const AudioVisualizerIcon = styled.div`
  font-size: 48px;
  color: ${palette.white};
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

export const AudioTestButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
`;

export const DeviceSelectorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  max-width: 400px;
`;

export const DeviceSelectorLabel = styled.label`
  font-size: 15px;
  font-weight: 600;
  color: ${palette.white};
  text-align: left;
  letter-spacing: 0.2px;
`;

/** Estilo compartido por los selectores de micrófono y altavoz. */
const selectStyles = css`
  width: 100%;
  padding: 12px 40px 12px 16px;
  border: 1.5px solid ${palette.border};
  border-radius: ${radii.sm};
  background: ${palette.white};
  color: ${palette.navy};
  font-size: 15px;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.3s ease;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  box-shadow: 0 1px 3px ${palette.shadowLight};
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 14 14'%3E%3Cpath fill='%232C3E50' d='M7 10L2 5h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;

  &:focus {
    outline: none;
    border-color: ${palette.red};
    box-shadow: 0 0 0 3px ${palette.redGlowSoft};
    background-color: ${palette.white};
  }

  &:hover {
    border-color: ${palette.navy};
    background-color: ${palette.surfaceMuted};
  }

  option {
    padding: 12px;
    background: ${palette.white};
    color: ${palette.navy};
    font-size: 15px;
  }
`;

export const DeviceSelector = styled.select`
  ${selectStyles};
`;

export const SpeakerSelectorRow = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  max-width: 400px;
  align-items: flex-end;
  margin-bottom: 0;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const SpeakerSelectorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  width: 100%;
`;

export const SpeakerSelectorLabel = styled.label`
  font-size: 15px;
  font-weight: 600;
  color: ${palette.white};
  text-align: left;
  letter-spacing: 0.2px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const SpeakerSelectorIcon = styled.span`
  display: flex;
  align-items: center;
  color: ${palette.white};
`;

export const SpeakerSelector = styled.select`
  ${selectStyles};
`;
